import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Bits";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const S = 512;

function Cropper({ img, onCancel, onSave, busy }) {
  const ref = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [x, setX] = useState(50);
  const [y, setY] = useState(50);
  useEffect(() => {
    const ctx = ref.current.getContext("2d");
    const side = Math.min(img.width, img.height) / zoom;
    ctx.clearRect(0, 0, S, S);
    ctx.drawImage(img, (img.width - side) * (x / 100), (img.height - side) * (y / 100), side, side, 0, 0, S, S);
  }, [img, zoom, x, y]);
  const save = () => ref.current.toBlob((b) => b && onSave(b), "image/jpeg", 0.9);
  const slider = (label, v, set, min, max, step, id) => (
    <label className="block text-xs text-slate-400">{label}<input type="range" min={min} max={max} step={step} value={v} onChange={(e) => set(Number(e.target.value))} className="mt-1 w-full accent-[#B7F52A]" data-testid={id} /></label>
  );
  return (
    <div className="space-y-3 rounded-xl border border-white/10 p-4" data-testid="avatar-cropper">
      <canvas ref={ref} width={S} height={S} className="mx-auto h-44 w-44 rounded-full border-2 border-[#B7F52A]" aria-label="Aperçu de la photo recadrée" />
      {slider("Zoom", zoom, setZoom, 1, 3, 0.05, "avatar-zoom")}
      {slider("Horizontal", x, setX, 0, 100, 1, "avatar-x")}
      {slider("Vertical", y, setY, 0, 100, 1, "avatar-y")}
      <div className="flex gap-2"><button type="button" disabled={busy} onClick={save} className="mpl-btn-primary" data-testid="avatar-upload-save">Enregistrer la photo</button>
        <button type="button" onClick={onCancel} className="mpl-btn-ghost" data-testid="avatar-upload-cancel">Annuler</button></div>
    </div>
  );
}

export default function AvatarUpload() {
  const { user, setUser } = useAuth();
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!TYPES.includes(f.type)) return toast.error("Format accepté : JPEG, PNG ou WebP.");
    if (f.size > 5 * 1024 * 1024) return toast.error("Image trop lourde : 5 Mo maximum.");
    const url = URL.createObjectURL(f);
    const im = new Image();
    im.onload = () => { setImg(im); URL.revokeObjectURL(url); };
    im.onerror = () => toast.error("Image illisible.");
    im.src = url;
  };
  const upload = async (blob) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", blob, "avatar.jpg");
    try { const { data } = await api.post("/me/avatar", fd, { timeout: 60000 }); setUser(data); setImg(null); toast.success("Photo enregistrée"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { const { data } = await api.delete("/me/avatar"); setUser(data); toast.success("Photo supprimée"); } catch (e) { toast.error(errMsg(e)); }
  };
  const hasPhoto = user.avatar?.startsWith("img:");
  return (
    <div className="space-y-3" data-testid="avatar-upload">
      <div className="flex items-center gap-3">
        <Avatar user={user} size={56} />
        <button type="button" onClick={() => input.current.click()} className="mpl-btn-ghost" data-testid="avatar-upload-button"><Camera size={15} /> {hasPhoto ? "Remplacer la photo" : "Choisir une photo"}</button>
        {hasPhoto && <button type="button" onClick={remove} className="flex items-center gap-1 text-xs font-semibold text-red-300" data-testid="avatar-remove-button"><Trash2 size={13} /> Supprimer</button>}
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="avatar-file-input" />
      </div>
      <p className="text-[11px] text-slate-500">JPEG, PNG ou WebP, 5 Mo maximum. La photo est recadrée, compressée et ses métadonnées (lieu, appareil) sont effacées. Seuls les membres de vos ligues la voient.</p>
      {img && <Cropper img={img} busy={busy} onCancel={() => setImg(null)} onSave={upload} />}
    </div>
  );
}
